/**
 * Worker Pool Manager
 * Manages a pool of geometry workers for parallel processing
 */
import * as Comlink from 'comlink';
import GeometryWorker from '../workers/geometry.worker.js?worker';
import * as GeometryOperations from './geometryOperations.js';

// Debug mode flag
const DEBUG = false;

// Maximum queue size to prevent memory issues from backed-up operations
const MAX_QUEUE_SIZE = 8;

class WorkerPool {
  constructor(poolSize = 4) {
    this.poolSize = poolSize;
    this.workers = [];
    this.queue = [];
    this.hasWorkerSupport = this.checkWorkerSupport();
    this.stats = {
      totalOperations: 0,
      totalTime: 0,
      operationCounts: {},
      averageTimes: {}
    };

    // Track active operations with abort controllers for proper cancellation
    this.activeOperations = new Map(); // Map<operationId, {worker, signal, startTime}>
    this.operationIdCounter = 0;

    if (this.hasWorkerSupport) {
      this.initializeWorkers();
    } else {
      console.warn('Web Workers not supported - falling back to main thread execution');
    }
  }

  /**
   * Check if Web Workers are supported
   */
  checkWorkerSupport() {
    return typeof Worker !== 'undefined' && typeof window !== 'undefined';
  }

  /**
   * Initialize worker pool
   */
  initializeWorkers() {
    for (let i = 0; i < this.poolSize; i++) {
      try {
        const worker = new GeometryWorker();
        const api = Comlink.wrap(worker);

        this.workers.push({
          id: i,
          worker,
          api,
          busy: false,
          currentOperation: null,
          operationStartTime: null
        });

        if (DEBUG) {
          console.log(`[WorkerPool] Initialized worker ${i}`);
        }
      } catch (error) {
        console.error(`[WorkerPool] Failed to initialize worker ${i}:`, error);
      }
    }

    if (this.workers.length === 0) {
      console.warn('[WorkerPool] No workers initialized - falling back to main thread');
      this.hasWorkerSupport = false;
    }
  }

  /**
   * Cancel all queued operations
   * This prevents the queue from blocking when new operations come in
   */
  flushQueue() {
    if (this.queue.length > 0) {
      if (DEBUG) {
        console.log(`[WorkerPool] Flushing queue with ${this.queue.length} pending operations`);
      }
      this.queue = [];
    }
  }

  /**
   * Cancel all active operations and clear queue
   * Used when user wants to stop all computation
   */
  cancelAll() {
    if (DEBUG) {
      console.log(`[WorkerPool] Cancelling all ${this.activeOperations.size} active operations`);
    }

    // Abort all active operations
    this.activeOperations.forEach(({ signal }) => {
      if (!signal.aborted) {
        signal.abort();
      }
    });

    // Clear the queue
    this.flushQueue();
  }

  /**
   * Get an available worker
   * @returns {Promise<number>} Worker ID
   */
  async getAvailableWorker() {
    // Check for immediately available worker
    for (let i = 0; i < this.workers.length; i++) {
      if (!this.workers[i].busy) {
        this.workers[i].busy = true;
        return i;
      }
    }

    // OPTIMIZATION: Check queue size to prevent memory issues
    // If queue is too large, reject new operations
    if (this.queue.length >= MAX_QUEUE_SIZE) {
      if (DEBUG) {
        console.warn(`[WorkerPool] Queue exceeded max size (${this.queue.length}/${MAX_QUEUE_SIZE}). Flushing oldest operations.`);
      }
      // Keep only the most recent operations, discard old ones
      this.queue = this.queue.slice(-Math.floor(MAX_QUEUE_SIZE / 2));
    }

    // No workers available - wait for one to become free
    return new Promise((resolve) => {
      this.queue.push(resolve);
    });
  }

  /**
   * Release a worker back to the pool
   * @param {number} workerId - Worker ID
   */
  releaseWorker(workerId) {
    if (workerId < 0 || workerId >= this.workers.length) return;

    const worker = this.workers[workerId];
    worker.busy = false;
    worker.currentOperation = null;

    // Process queued operations
    if (this.queue.length > 0) {
      const resolve = this.queue.shift();
      worker.busy = true;
      resolve(workerId);
    }

    if (DEBUG) {
      console.log(`[WorkerPool] Released worker ${workerId}, queue size: ${this.queue.length}`);
    }
  }

  /**
   * Execute an operation using the worker pool
   * @param {string} operation - Operation name
   * @param {Array} args - Operation arguments
   * @param {AbortSignal} signal - Optional abort signal
   * @returns {Promise<any>} - Operation result
   */
  async execute(operation, ...args) {
    // Extract abort signal if provided as last argument
    let signal = null;
    let operationArgs = args;

    if (args.length > 0 && args[args.length - 1] instanceof AbortSignal) {
      signal = args[args.length - 1];
      operationArgs = args.slice(0, -1);
    }

    // Check if operation was cancelled before starting
    if (signal?.aborted) {
      throw new DOMException('Operation cancelled', 'AbortError');
    }

    const startTime = performance.now();
    const operationId = ++this.operationIdCounter;

    // Fallback to main thread if workers not available
    if (!this.hasWorkerSupport || this.workers.length === 0) {
      return this.executeOnMainThread(operation, ...operationArgs);
    }

    // Get available worker
    const workerId = await this.getAvailableWorker();

    // Check again if operation was cancelled while waiting
    if (signal?.aborted) {
      this.releaseWorker(workerId);
      throw new DOMException('Operation cancelled', 'AbortError');
    }

    const worker = this.workers[workerId];
    worker.currentOperation = operation;
    worker.operationStartTime = startTime;

    // Track this operation for potential cancellation
    // If no signal provided, we don't need to track it (it won't be cancelled)
    if (signal) {
      this.activeOperations.set(operationId, {
        worker: workerId,
        signal,
        startTime
      });
    }

    try {
      if (DEBUG) {
        console.log(`[WorkerPool] Operation ${operationId}: Worker ${workerId} executing ${operation}`);
      }

      let result;

      // If abort signal provided, race the operation against abort
      if (signal) {
        // Create promise that rejects when aborted
        const abortPromise = new Promise((_, reject) => {
          const abortHandler = () => {
            if (DEBUG) {
              console.log(`[WorkerPool] Operation ${operationId}: Aborting ${operation} on worker ${workerId}`);
            }
            reject(new DOMException('Operation cancelled', 'AbortError'));
          };

          signal.addEventListener('abort', abortHandler, { once: true });
        });

        // Race between operation and abort
        // FIX: We now properly track the operation and release resources when aborted
        result = await Promise.race([
          worker.api[operation](...operationArgs),
          abortPromise
        ]);
      } else {
        // No signal, execute normally
        result = await worker.api[operation](...operationArgs);
      }

      // Update stats
      const duration = performance.now() - startTime;
      this.updateStats(operation, duration);

      if (DEBUG) {
        console.log(`[WorkerPool] Operation ${operationId}: Worker ${workerId} completed ${operation} in ${duration.toFixed(2)}ms`);
      }

      return result;
    } catch (error) {
      // Check if it's an abort error
      if (signal?.aborted) {
        if (DEBUG) {
          console.log(`[WorkerPool] Operation ${operationId}: Caught abort error for ${operation}`);
        }
        throw new DOMException('Operation cancelled', 'AbortError');
      }

      console.error(`[WorkerPool] Operation ${operationId}: Error in worker ${workerId} executing ${operation}:`, error);
      throw error;
    } finally {
      // Clean up operation tracking
      this.activeOperations.delete(operationId);
      this.releaseWorker(workerId);
    }
  }

  /**
   * Execute operation on main thread (fallback)
   * @param {string} operation - Operation name
   * @param {Array} args - Operation arguments
   * @returns {Promise<any>} - Operation result
   */
  async executeOnMainThread(operation, ...args) {
    if (DEBUG) {
      console.log(`[WorkerPool] Executing ${operation} on main thread (fallback)`);
    }

    const startTime = performance.now();

    try {
      let result;

      // Map worker operations to main thread equivalents
      switch (operation) {
        case 'computeCarveReliefDeformation':
          result = GeometryOperations.computeVertexDeformation(...args);
          break;

        case 'applyVertexDisplacement':
          // This needs special handling - implement if needed
          throw new Error('applyVertexDisplacement not implemented for main thread');

        case 'smoothVertices': {
          const [positions, indices, vertexIndices, iterations, strength] = args;
          const vertexCount = positions.length / 3;
          const adjacency = GeometryOperations.buildAdjacencyList(indices, vertexCount);
          const smoothedPositions = GeometryOperations.applyLaplacianSmoothing(
            new Float32Array(positions),
            adjacency,
            Array.from(vertexIndices),
            iterations,
            strength
          );
          // Note: Normals recomputation would need to be done separately
          result = { positions: smoothedPositions, normals: null };
          break;
        }

        default:
          throw new Error(`Unknown operation: ${operation}`);
      }

      const duration = performance.now() - startTime;
      this.updateStats(operation, duration);

      if (DEBUG) {
        console.log(`[WorkerPool] Main thread completed ${operation} in ${duration.toFixed(2)}ms`);
      }

      return result;
    } catch (error) {
      console.error(`[WorkerPool] Error executing ${operation} on main thread:`, error);
      throw error;
    }
  }

  /**
   * Update operation statistics
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in milliseconds
   */
  updateStats(operation, duration) {
    this.stats.totalOperations++;
    this.stats.totalTime += duration;

    if (!this.stats.operationCounts[operation]) {
      this.stats.operationCounts[operation] = 0;
      this.stats.averageTimes[operation] = 0;
    }

    const count = this.stats.operationCounts[operation];
    const avgTime = this.stats.averageTimes[operation];

    this.stats.operationCounts[operation] = count + 1;
    this.stats.averageTimes[operation] = (avgTime * count + duration) / (count + 1);
  }

  /**
   * Get pool statistics
   * @returns {Object} Statistics
   */
  getStats() {
    return {
      ...this.stats,
      poolSize: this.poolSize,
      activeWorkers: this.workers.filter(w => w.busy).length,
      activeOperations: this.activeOperations.size,
      queueSize: this.queue.length,
      maxQueueSize: MAX_QUEUE_SIZE,
      hasWorkerSupport: this.hasWorkerSupport
    };
  }

  /**
   * Get pool status (useful for debugging UI)
   * @returns {Object} Status
   */
  getStatus() {
    return {
      workers: this.workers.map(w => ({
        id: w.id,
        busy: w.busy,
        currentOperation: w.currentOperation,
        duration: w.operationStartTime ? performance.now() - w.operationStartTime : 0
      })),
      activeOperations: Array.from(this.activeOperations.entries()).map(([id, info]) => ({
        id,
        worker: info.worker,
        duration: performance.now() - info.startTime
      })),
      queueSize: this.queue.length,
      maxQueueSize: MAX_QUEUE_SIZE,
      hasWorkerSupport: this.hasWorkerSupport
    };
  }

  /**
   * Terminate all workers and clean up
   */
  terminate() {
    for (const workerInfo of this.workers) {
      try {
        workerInfo.worker.terminate();
      } catch (error) {
        console.error('[WorkerPool] Error terminating worker:', error);
      }
    }

    this.workers = [];
    this.queue = [];

    if (DEBUG) {
      console.log('[WorkerPool] All workers terminated');
    }
  }

  /**
   * Warm up workers by executing a small operation
   * This helps reduce latency on first real operation
   */
  async warmUp() {
    if (!this.hasWorkerSupport) return;

    const dummyPositions = new Float32Array([0, 0, 0, 1, 0, 0, 0, 1, 0]);
    const dummyIndices = new Uint32Array([0, 1, 2]);

    try {
      await this.execute('calculateMeshStats', dummyPositions, dummyIndices);
      if (DEBUG) {
        console.log('[WorkerPool] Warm-up complete');
      }
    } catch (error) {
      console.warn('[WorkerPool] Warm-up failed:', error);
    }
  }
}

// Create singleton instance with 4 workers (as requested)
export const geometryWorkerPool = new WorkerPool(4);

// Warm up workers on module load
if (typeof window !== 'undefined') {
  geometryWorkerPool.warmUp();
}

// Clean up workers on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    geometryWorkerPool.terminate();
  });
}

// ============================================================================
// DEBUGGING API - Available in browser console for monitoring worker pool
// ============================================================================
if (typeof window !== 'undefined') {
  /**
   * Expose worker pool debugging API to window for DevTools console access
   * Usage in browser console:
   *   window.__workerDebug.status()      - Quick status overview
   *   window.__workerDebug.details()     - Detailed worker info
   *   window.__workerDebug.stats()       - Operation statistics
   *   window.__workerDebug.watch()       - Start continuous monitoring
   *   window.__workerDebug.unwatch()     - Stop monitoring
   */
  let monitoringInterval = null;

  window.__workerDebug = {
    /**
     * Get quick status overview
     */
    status() {
      const stats = geometryWorkerPool.getStats();
      const busyWorkers = stats.activeWorkers;
      const totalWorkers = stats.poolSize;
      const queueSize = stats.queueSize;
      const activeOps = stats.activeOperations;

      console.clear();
      console.log('%c⚙️  WORKER POOL STATUS', 'font-size: 14px; font-weight: bold; color: #2196F3;');
      console.log(`%cBusy Workers: ${busyWorkers}/${totalWorkers}`, `color: ${busyWorkers > 0 ? '#FF9800' : '#4CAF50'};`);
      console.log(`%cQueue Size: ${queueSize}/${stats.maxQueueSize}`, `color: ${queueSize > 4 ? '#F44336' : '#4CAF50'};`);
      console.log(`%cActive Operations: ${activeOps}`, `color: #2196F3;`);
      console.log(`%cTotal Operations: ${stats.totalOperations}`, 'color: #666;');

      return {
        busyWorkers,
        totalWorkers,
        queueSize,
        maxQueueSize: stats.maxQueueSize,
        activeOperations: activeOps,
        totalOperations: stats.totalOperations
      };
    },

    /**
     * Get detailed worker information
     */
    details() {
      const status = geometryWorkerPool.getStatus();

      console.clear();
      console.log('%c📊 WORKER POOL DETAILS', 'font-size: 14px; font-weight: bold; color: #2196F3;');

      // Worker details
      console.log('%c=== WORKERS ===', 'font-weight: bold; color: #1976D2;');
      status.workers.forEach(w => {
        const state = w.busy ? '🔴 BUSY' : '🟢 IDLE';
        const op = w.currentOperation ? ` [${w.currentOperation}]` : '';
        const duration = w.duration > 0 ? ` (${w.duration.toFixed(0)}ms)` : '';
        console.log(`Worker ${w.id}: ${state}${op}${duration}`);
      });

      // Active operations
      if (status.activeOperations.length > 0) {
        console.log('%c=== ACTIVE OPERATIONS ===', 'font-weight: bold; color: #1976D2;');
        status.activeOperations.forEach(op => {
          console.log(`Op #${op.id} on Worker ${op.worker}: ${op.duration.toFixed(0)}ms`);
        });
      }

      // Queue status
      console.log('%c=== QUEUE ===', 'font-weight: bold; color: #1976D2;');
      console.log(`Pending: ${status.queueSize}/${status.maxQueueSize}`);

      return status;
    },

    /**
     * Get operation statistics
     */
    stats() {
      const stats = geometryWorkerPool.getStats();

      console.clear();
      console.log('%c📈 OPERATION STATISTICS', 'font-size: 14px; font-weight: bold; color: #2196F3;');
      console.log(`Total Operations: ${stats.totalOperations}`);
      console.log(`Total Time: ${(stats.totalTime / 1000).toFixed(2)}s`);
      console.log(`Average Time: ${(stats.totalTime / Math.max(stats.totalOperations, 1)).toFixed(2)}ms`);

      if (Object.keys(stats.operationCounts).length > 0) {
        console.log('%c=== OPERATION BREAKDOWN ===', 'font-weight: bold; color: #1976D2;');
        Object.entries(stats.operationCounts).forEach(([op, count]) => {
          const avgTime = stats.averageTimes[op];
          console.log(`${op}: ${count}x (avg ${avgTime.toFixed(2)}ms)`);
        });
      }

      return stats;
    },

    /**
     * Start continuous monitoring (updates every 500ms)
     */
    watch(intervalMs = 500) {
      if (monitoringInterval) {
        console.log('Already monitoring. Call unwatch() to stop.');
        return;
      }

      console.log('%c👁️  MONITORING STARTED', 'font-size: 12px; font-weight: bold; color: #4CAF50;');
      console.log('Type window.__workerDebug.unwatch() to stop');

      monitoringInterval = setInterval(() => {
        const status = this.status();
      }, intervalMs);

      // Store interval reference
      window.__workerDebug._interval = monitoringInterval;
    },

    /**
     * Stop continuous monitoring
     */
    unwatch() {
      if (window.__workerDebug._interval) {
        clearInterval(window.__workerDebug._interval);
        window.__workerDebug._interval = null;
        console.log('%c👁️  MONITORING STOPPED', 'font-size: 12px; font-weight: bold; color: #F44336;');
      }
    },

    /**
     * Cancel all operations and flush queue
     */
    cancel() {
      geometryWorkerPool.cancelAll();
      console.log('%c⛔ CANCELLED ALL OPERATIONS', 'font-size: 12px; font-weight: bold; color: #F44336;');
      this.status();
    },

    /**
     * Flush pending queue
     */
    flushQueue() {
      geometryWorkerPool.flushQueue();
      console.log('%c🗑️  QUEUE FLUSHED', 'font-size: 12px; font-weight: bold; color: #FF9800;');
      this.status();
    },

    /**
     * Enable/disable debug logging
     */
    setDebug(enabled) {
      // Note: This sets the module-level DEBUG flag
      // You'd need to restart the module for this to take full effect
      // Instead, we'll log helpful info
      console.log(`%c🐛 Debug info:`, 'font-weight: bold;');
      console.log('Enable debug logging by modifying WorkerPool.js:10 and reloading');
      console.log('Change: const DEBUG = false;');
      console.log('To:     const DEBUG = true;');
    },

    /**
     * Help command
     */
    help() {
      console.clear();
      console.log('%c📚 WORKER POOL DEBUG API', 'font-size: 14px; font-weight: bold; color: #2196F3;');
      console.log('%c=== QUICK COMMANDS ===', 'font-weight: bold;');
      console.log('window.__workerDebug.status()   - Quick status overview');
      console.log('window.__workerDebug.details()  - Detailed worker info');
      console.log('window.__workerDebug.stats()    - Operation statistics');
      console.log('window.__workerDebug.watch()    - Monitor every 500ms');
      console.log('window.__workerDebug.unwatch()  - Stop monitoring');
      console.log('');
      console.log('%c=== CONTROL COMMANDS ===', 'font-weight: bold;');
      console.log('window.__workerDebug.cancel()   - Cancel all operations');
      console.log('window.__workerDebug.flushQueue() - Clear pending operations');
      console.log('');
      console.log('%c=== TIPS ===', 'font-weight: bold;');
      console.log('• Use .watch() during slider changes to see queue buildup');
      console.log('• Red busy workers indicate heavy computation');
      console.log('• Large queue size indicates bottleneck');
      console.log('• Monitor.stats() to see average operation times');
    }
  };

  // Log that debug API is available
  console.log('%c✅ Worker Pool Debug API Available', 'font-size: 12px; color: #4CAF50; font-weight: bold;');
  console.log('Type: window.__workerDebug.help()');
}

export default WorkerPool;
